import { useContext } from 'react';

import { Checkbox, HStack, Input, Select, Stack, Text, VStack } from '@chakra-ui/react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { DealStateEnum, DealStateLabelMap } from '../../gql/dealGql';
import { DealType } from '../../gql/generated/graphql';

import CompletedDealsTable from '../../components/CompletedDealsTables/CompletedDealsTable';
import LeaseEndContainer from '../../components/Container/LEContainer';
import ButtonsRow from '../../components/Dashboards/ButtonsRow';
import UserAutocomplete from '../../components/shared/UserAutocomplete';

import { LDFlags } from '../../constants/experiments';
import { PermissionEnum } from '../../constants/permissions';
import { FiltersActionKind } from '../../globalFiltersUtils';
import useFlag from '../../hooks/useFlag';
import { Can } from '../../libs/Can';
import { FiltersContext } from '../../libs/contextLib';
import { provincesArray } from '../../libs/provinces';
import { states } from '../../libs/states';
import { formatDateISO } from '../../libs/utils';

const CompletedDealsPage = () => {
  const {
    filters: {
      completed: { endDate, startDate, dealState, addressState, user },
      global: { showOnlyUnread, showAssignedToMe, types },
    },
    dispatch: dispatchFilters,
  } = useContext(FiltersContext);
  const canadaEnabled = !!useFlag(LDFlags.DD_CANADA_DEALS);

  const sharedDatePickerProps: Partial<ReactDatePickerProps> = {
    maxDate: new Date(),
    autoComplete: 'off',
    showYearDropdown: true,
    showMonthDropdown: true,
    showPopperArrow: false,
    popperProps: { strategy: 'fixed' },
    portalId: 'root',
    customInput: <Input bgColor="white" />,
  };

  return (
    <>
      <ButtonsRow />
      <LeaseEndContainer>
        <HStack gap={4} mb={4} mx={10}>
          {/* Start Date */}
          <VStack align="start">
            <Text>Start Date</Text>
            <ReactDatePicker
              {...sharedDatePickerProps}
              selected={startDate}
              onChange={(date) => {
                if (date) {
                  dispatchFilters({ type: 'setStartDate', payload: formatDateISO(date) });
                }
              }}
            />
          </VStack>
          {/* End Date */}
          <VStack align="start">
            <Text>End Date</Text>
            <ReactDatePicker
              {...sharedDatePickerProps}
              selected={endDate}
              onChange={(date) => {
                if (date) {
                  dispatchFilters({ type: 'setEndDate', payload: formatDateISO(date) });
                }
              }}
              minDate={startDate}
            />
          </VStack>

          {/* Deal State Filter */}
          <VStack spacing="1" mt={4} align="start">
            <Text>Deal State</Text>
            <Select
              w="270px"
              bgColor="white"
              name="state"
              onChange={(e) => dispatchFilters({ type: 'setDealState', payload: e.target.value })}
            >
              <option value="all">ALL</option>
              {Object.values(DealStateEnum).map((stateValue) => (
                <option key={stateValue} value={stateValue}>
                  {DealStateLabelMap[stateValue as keyof typeof DealStateLabelMap]}
                </option>
              ))}
            </Select>
          </VStack>
          {/* US State / Province Filter */}
          <VStack spacing="1" mt={4} align="start">
            <Text>{canadaEnabled ? 'State / Province' : 'State'}</Text>
            <Select
              w="80px"
              bgColor="white"
              name="state"
              onChange={(e) =>
                dispatchFilters({ type: 'setAddressState', payload: e.target.value })
              }
            >
              <option value="all">ALL</option>
              {Object.keys(states).map((stateKey) => (
                <option key={stateKey} value={states[stateKey as keyof typeof states]}>
                  {states[stateKey as keyof typeof states]}
                </option>
              ))}
              {canadaEnabled
                ? provincesArray.map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))
                : null}
            </Select>
          </VStack>
          {/* User Filter */}
          <Can I={PermissionEnum.SeeUsers}>
            <VStack spacing="1" mt={4} align="start">
              <Text>User</Text>
              <UserAutocomplete
                includeEmptyOption
                emptyOptionLabel="ALL"
                user={user}
                w="270px"
                setUser={(selectedUser) =>
                  dispatchFilters({ type: 'setUser', payload: selectedUser })
                }
              />
            </VStack>
          </Can>
          <VStack spacing={0} align="start">
            <Checkbox
              onChange={(e) =>
                dispatchFilters({
                  type: FiltersActionKind.SET_SHOW_ONLY_UNREAD,
                  payload: e.target.checked,
                })
              }
              isChecked={showOnlyUnread}
            >
              Unread Messages
            </Checkbox>
            <Can I={PermissionEnum.SeeAssignedToMeFilter}>
              <Checkbox
                isChecked={showAssignedToMe}
                onChange={(e) =>
                  dispatchFilters({
                    type: FiltersActionKind.SET_SHOW_ASSIGNED_TO_ME,
                    payload: e.target.checked,
                  })
                }
              >
                Assigned to Me
              </Checkbox>
            </Can>
          </VStack>
        </HStack>
        <Stack spacing={8}>
          {(types.includes(DealType.Buyout) ||
            types.includes(DealType.Refi) ||
            types.includes(DealType.Marketplace) ||
            !types.length) && (
            <CompletedDealsTable
              startDate={startDate}
              endDate={endDate}
              user={user}
              dealState={dealState}
              addressState={addressState}
              notifications={showOnlyUnread}
            />
          )}
        </Stack>
      </LeaseEndContainer>
    </>
  );
};

export default CompletedDealsPage;
